import React, { useEffect, useState, useCallback } from 'react';
import {
    HStack,
    useToast,
    Button,
    Menu,
    MenuContent,
    MenuRadioGroup,
    MenuRadioItem,
    MenuTrigger,
} from '@cad-arc';
import { createApiErrorHandler } from '@m43/core';
import { useApiClient } from '~/api-client';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useCallTakerStation } from './use-call-taker-station';
import { CallTakerStationOption, CallTakerStationResource } from '@mark43/cad-api';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { useApplicationType } from '../common/hooks/use-application-type';

export function CallTakerStationMenu(): React.ReactElement {
    const { isElectron } = useApplicationType();
    const apiClient = useApiClient();
    const stationContext = useCallTakerStation();
    const stationOptions: CallTakerStationOption[] = useBootstrapState().selectCallTakerStationView
        .callTakerStations;

    const toast = useToast();
    const assignUserToCallStation = useCallback(
        (stationId: string) => {
            return apiClient
                .request<CallTakerStationResource.AssignUserToCallStationCadNext>({
                    method: 'POST',
                    url: `call_taker_station/assign/cad_next/${stationId}/`,
                })
                .then((response) => response.data.data);
        },
        [apiClient]
    );
    const unassignUserFromCallStation = useCallback(() => {
        return apiClient
            .request<CallTakerStationResource.UnassignUserFromCallStation>({
                method: 'DELETE',
                url: 'call_taker_station/unassign/',
            })
            .then((response) => response.data.data);
    }, [apiClient]);

    const handleClearStationClick = useCallback(() => {
        if (!stationContext?.currentStation) {
            return;
        }
        setIsMenuEnabled(false);

        if (isElectron) {
            stationContext.assignCallTakerStationElectron?.();
        } else {
            unassignUserFromCallStation()
                .then((success) => {
                    if (success) {
                        stationContext.setCurrentStation?.(undefined);
                    }
                })
                .catch(
                    createApiErrorHandler(() => {
                        setIsMenuEnabled(true);
                        toast({
                            status: 'error',
                            description: errorOccurredWhile('unassigning from station'),
                        });
                    })
                );
        }
    }, [isElectron, stationContext, toast, unassignUserFromCallStation]);

    const [isMenuEnabled, setIsMenuEnabled] = useState<boolean>(true);

    useEffect(
        function reenableMenuAfterStationChanged() {
            setIsMenuEnabled(true);
        },
        [stationContext?.currentStation]
    );

    useEffect(function registerAssignCallTakerStationElectronFailedHandler() {
        const handler = function reenableMenuAfterStationChangeFailed() {
            setIsMenuEnabled(true);
        };
        stationContext?.registerAssignCallTakerStationElectronFailedHandler?.(handler);

        return () => {
            stationContext?.registerAssignCallTakerStationElectronFailedHandler?.(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Menu>
            <MenuTrigger asChild>
                <Button variant="ghost" data-testid="CALL_TAKER_STATION_ID">
                    Station ID: {stationContext.currentStation?.label ?? 'None'}
                </Button>
            </MenuTrigger>
            <MenuContent avoidCollisions style={{ overflowY: 'auto', maxHeight: '600px' }}>
                <HStack justify="flex-end">
                    <Button
                        isDisabled={!isMenuEnabled || !stationContext?.currentStation}
                        variant="ghost"
                        size="sm"
                        data-testid="CALL_TAKER_STATION_ID_RESET_BUTTON"
                        onClick={() => handleClearStationClick()}
                    >
                        Clear Station ID
                    </Button>
                </HStack>
                <MenuRadioGroup
                    value={stationContext.currentStation?.value.toString()}
                    onValueChange={(value) => {
                        const selectedStation = stationOptions.find((stationOption) => {
                            return stationOption.value.toString() === value;
                        });

                        const isDifferentStation =
                            selectedStation &&
                            selectedStation.value.toString() !==
                                stationContext.currentStation?.value.toString();

                        if (!isDifferentStation) {
                            return;
                        }

                        setIsMenuEnabled(false);

                        assignUserToCallStation(value)
                            .then((callTakerStationOption) => {
                                if (isElectron) {
                                    stationContext.assignCallTakerStationElectron?.(
                                        selectedStation.value
                                    );
                                }
                                if (callTakerStationOption) {
                                    stationContext.setCurrentStation?.(callTakerStationOption);
                                }
                            })
                            .catch(
                                createApiErrorHandler(() => {
                                    setIsMenuEnabled(true);
                                    toast({
                                        status: 'error',
                                        description: errorOccurredWhile('assigning station'),
                                    });
                                })
                            );
                    }}
                >
                    {stationOptions.map((stationOption) => (
                        <MenuRadioItem
                            isDisabled={!isMenuEnabled}
                            key={stationOption.value}
                            value={stationOption.value.toString()}
                        >
                            {stationOption.label}
                        </MenuRadioItem>
                    ))}
                </MenuRadioGroup>
            </MenuContent>
        </Menu>
    );
}
